import { ChangeDetectionStrategy, Component, computed, input, model, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { collectTreeFieldLeaves, TreeFieldNode } from './tree-field-node';
import { SignalFieldBaseDirective } from '../signal-field-base.directive';

/**
 * Low-level tree for signal forms.
 *
 * The selection model is the reactive-forms one, which answers the only real design
 * question here: **only leaves are selectable**, and there is no parent-to-child cascade.
 * Clicking a branch expands or collapses it; a branch shows a tick when any leaf beneath
 * it is selected. So the value is a flat `string[]` of selected *leaf* values — the same
 * shape the dropdown carries — and the slice never holds a branch.
 *
 * ```html
 * <pace-core-tree-field [formField]="signalForm.taskGroups" [nodes]="taskGroups()" />
 * ```
 *
 * Expansion is deliberately *not* part of the value: which branches are open is view
 * state, so it lives in a signal here rather than in the store. That is also why
 * `TreeFieldNode` carries no `isExpanded`, unlike `TreeViewNode`.
 *
 * "At least one selection" belongs in the schema as `minLength(path, 1)` and the cap as
 * `maxLength`, both of which reach this component through the `FormUiControl` contract —
 * so `maxSelections` only has to be given when no schema rule sets it.
 *
 * Two departures from `TreeViewComponent`/`TreeComponent`:
 *
 * - it takes the top-level nodes rather than a single root node whose own label is never
 *   rendered. The legacy tree is handed a root and draws `root.children`, which means
 *   fixtures have to invent a root that nobody sees.
 * - collapsing everything is a button of its own, shown while anything is expanded,
 *   instead of one that appears inside whichever node happens to be selected.
 *
 * Not carried over: `dragndrop`, which `TreeViewModelOptions` declares and nothing reads.
 */
@Component({
    selector: 'pace-tree-field',
    templateUrl: './tree-field.component.html',
    styleUrls: ['./tree-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FaIconComponent, NgTemplateOutlet],
})
export class TreeFieldComponent extends SignalFieldBaseDirective<string[]> {
    /** the selected leaf values, in selection order */
    public readonly value = model<string[]>([]);

    // --- `FormUiControl` inputs, all bound automatically by `[formField]` -----

    /** lower bound on the number of selections, from the schema's `minLength` rule */
    public readonly minLength = input<number | undefined>(undefined);

    /** upper bound on the number of selections, from the schema's `maxLength` rule */
    public readonly maxLength = input<number | undefined>(undefined);

    // --- own inputs ----------------------------------------------------------

    /** the top-level nodes; branches carry their own children */
    public readonly nodes = input<readonly TreeFieldNode[]>([]);

    /** 1 makes this a single-select; the schema's `maxLength` wins over it when smaller */
    public readonly maxSelections = input(1);

    public readonly faCheck = faCheck;

    /** values of the branches currently open — view state, not form data */
    private readonly expandedBranches = signal<ReadonlySet<string>>(new Set());

    /** the effective cap: whichever of the schema rule and the input is stricter */
    protected readonly selectionLimit = computed(() => Math.min(this.maxSelections(), this.maxLength() ?? Infinity));

    protected readonly isMultiSelect = computed(() => this.selectionLimit() > 1);

    private readonly selectedValues = computed(() => new Set(this.value()));

    protected readonly isAtLimit = computed(() => this.value().length >= this.selectionLimit());

    protected readonly anyExpanded = computed(() => this.expandedBranches().size > 0);

    /**
     * Values of the branches with at least one selected leaf beneath them, worked out in a
     * single walk so the template does not re-descend the tree for every node it draws.
     */
    private readonly branchesWithSelection = computed(() => {
        const selected = this.selectedValues();
        const marked = new Set<string>();

        const walk = (nodes: readonly TreeFieldNode[]): boolean =>
            nodes.reduce((anySelected, node) => {
                const children = node.children ?? [];
                const nodeCarriesSelection = children.length ? walk(children) : selected.has(node.value);
                if (children.length && nodeCarriesSelection) {
                    marked.add(node.value);
                }

                return anySelected || nodeCarriesSelection;
            }, false);

        walk(this.nodes());

        return marked;
    });

    public hasChildren(node: TreeFieldNode): boolean {
        return !!node.children?.length;
    }

    public isExpanded(node: TreeFieldNode): boolean {
        return this.expandedBranches().has(node.value);
    }

    public isSelected(node: TreeFieldNode): boolean {
        return this.selectedValues().has(node.value);
    }

    public hasSelectedDescendant(node: TreeFieldNode): boolean {
        return this.branchesWithSelection().has(node.value);
    }

    /**
     * Only leaves are selectable, and the cap only ever blocks a multi-select — a
     * single-select replaces whatever is there. Deselecting is always allowed.
     *
     * @param node the node to test
     * @returns whether clicking it would change the selection
     */
    public isSelectable(node: TreeFieldNode): boolean {
        if (this.hasChildren(node) || node.disabled) {
            return false;
        }
        if (!this.isMultiSelect()) {
            return true;
        }

        return !this.isAtLimit() || this.isSelected(node);
    }

    /**
     * One click target per node: a branch opens or closes, a leaf toggles its selection.
     *
     * @param node the node that was clicked
     */
    public activate(node: TreeFieldNode): void {
        if (!this.editable() || node.disabled) {
            return;
        }

        if (this.hasChildren(node)) {
            this.toggleExpanded(node);

            return;
        }

        this.select(node);
    }

    public collapseAll(): void {
        this.expandedBranches.set(new Set());
    }

    private toggleExpanded(node: TreeFieldNode): void {
        const expanded = new Set(this.expandedBranches());
        if (!expanded.delete(node.value)) {
            expanded.add(node.value);
        }

        this.expandedBranches.set(expanded);
    }

    private select(leaf: TreeFieldNode): void {
        if (!this.isSelectable(leaf)) {
            return;
        }

        const selected = this.value();
        if (this.isSelected(leaf)) {
            this.commit(selected.filter((entry) => entry !== leaf.value));
        } else {
            this.commit(this.isMultiSelect() ? [...selected, leaf.value] : [leaf.value]);
        }
    }

    /**
     * A click is an interaction, even though the rows never blur the way an input does.
     *
     * @param next the new selection
     */
    private commit(next: string[]): void {
        this.value.set(next);
        this.touched.set(true);
    }

    /** exposed for consumers that need the selectable set, e.g. to seed a selection */
    public readonly leaves = computed(() => collectTreeFieldLeaves(this.nodes()));
}
